import React, { useState, useEffect, useRef, useCallback } from "react";
import * as invApi from "./api/membersInvitations";

/**
 * Oeffentliche Registrierung nach Einladung (kein Admin-Shell, vor Login-Return in App).
 *
 * Flow (3 Schritte):
 *  1) "form": Local-Part waehlen
 *  2) "code": SMS-Code (an die in der Einladung hinterlegte Telefonnummer) eingeben
 *  3) Erfolg -> onRegistered(token)
 */
export default function MemberInviteRegister({ apiBase, onRegistered }) {
  const [inviteToken] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const t = (new URLSearchParams(window.location.search || "").get("token") || "").trim();
      return t || null;
    } catch {
      return null;
    }
  });
  const [preflightErr, setPreflightErr] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [step, setStep] = useState("form"); // "form" | "code"
  const [localPart, setLocalPart] = useState("");
  const [code, setCode] = useState("");
  const [avail, setAvail] = useState(null);
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!inviteToken) {
      setPreflightLoading(false);
      setPreflightErr("Kein Einladungslink. Bitte den vollstaendigen Link aus der E-Mail verwenden.");
      return;
    }
    (async () => {
      setPreflightErr("");
      setPreflightLoading(true);
      try {
        const d = await invApi.preflightInvite({ apiBase, token: inviteToken });
        if (d.email_masked) setEmailMasked(d.email_masked);
        if (d.phone_masked) setPhoneMasked(d.phone_masked);
      } catch (e) {
        setPreflightErr(e.message || "Ungueltige Einladung");
      } finally {
        setPreflightLoading(false);
      }
    })();
  }, [apiBase, inviteToken]);

  const runAvailability = useCallback(
    (lp) => {
      if (!inviteToken || lp.length < 2) {
        setAvail(null);
        return;
      }
      (async () => {
        try {
          const a = await invApi.checkAvailability({
            apiBase,
            token: inviteToken,
            localPart: lp,
          });
          setAvail(a);
        } catch (e) {
          setPreflightErr(e.message || "Einladung ungueltig");
        }
      })();
    },
    [apiBase, inviteToken],
  );

  useEffect(() => {
    if (!inviteToken) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const lp = localPart.trim().toLowerCase();
      runAvailability(lp);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localPart, inviteToken, runAvailability]);

  const requestSms = (e) => {
    if (e) e.preventDefault();
    setSubmitErr("");
    if (avail && avail.available === false) {
      setSubmitErr("Adresse ist nicht verfuegbar");
      return;
    }
    if (!localPart.trim()) {
      setSubmitErr("Bitte Adresse waehlen");
      return;
    }
    setSubmitting(true);
    (async () => {
      try {
        const res = await invApi.startInviteSms({
          apiBase,
          token: inviteToken,
          localPart: localPart.trim().toLowerCase(),
        });
        if (res && res.phone_masked) setPhoneMasked(res.phone_masked);
        setStep("code");
        setCode("");
      } catch (e) {
        setSubmitErr(e.message || "SMS konnte nicht versendet werden");
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const submitCode = (e, overrideCode) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitErr("");
    const c = (overrideCode != null ? overrideCode : code).trim();
    if (!/^\d{4,10}$/.test(c)) {
      setSubmitErr("Bitte 6-stelligen Code eingeben");
      return;
    }
    setSubmitting(true);
    (async () => {
      try {
        const res = await invApi.acceptInvite({
          apiBase,
          token: inviteToken,
          localPart: localPart.trim().toLowerCase(),
          code: c,
        });
        if (res.token) {
          onRegistered(res.token);
        } else {
          setSubmitErr("Unerwartete Antwort");
        }
      } catch (e) {
        setSubmitErr(e.message || "Code falsch oder abgelaufen");
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const onCodeChange = (raw) => {
    const digits = String(raw || "").replace(/\D+/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6 && !submitting) {
      submitCode(null, digits);
    }
  };

  if (!inviteToken) {
    return (
      <div className="admin_screen admin_screen--login">
        <div className="admin_login_card">
          <h1 className="admin_login_title">Einladung</h1>
          <p className="admin_error" style={{ margin: 0 }}>
            {preflightErr || "Kein Token in der Adresse."}
          </p>
        </div>
      </div>
    );
  }

  if (preflightLoading && !preflightErr) {
    return (
      <div className="admin_screen admin_screen--login">
        <div className="admin_login_card">
          <p className="admin_product_empty" style={{ margin: 0 }}>
            Einladung wird geprueft…
          </p>
        </div>
      </div>
    );
  }

  if (preflightErr && !emailMasked && !phoneMasked) {
    return (
      <div className="admin_screen admin_screen--login">
        <div className="admin_login_card">
          <h1 className="admin_login_title">Einladung</h1>
          <p className="admin_error" style={{ margin: 0 }}>
            {preflightErr}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin_screen admin_screen--login">
      <div className="admin_login_card" style={{ maxWidth: 420 }}>
        <div className="admin_login_logo_wrap">
          <img src="/favicon.svg" alt="" className="admin_login_logo" />
          <span className="admin_login_brand">Telephonio</span>
        </div>
        <h1 className="admin_login_title">
          {step === "form" ? "Member-Registrierung" : "SMS-Code eingeben"}
        </h1>
        {(emailMasked || phoneMasked) && (
          <p
            className="admin_product_desc"
            style={{ fontSize: "14px", margin: "0 0 12px" }}>
            Einladung an{" "}
            {phoneMasked ? <strong>{phoneMasked}</strong> : null}
            {phoneMasked && emailMasked ? " / " : null}
            {emailMasked ? <strong>{emailMasked}</strong> : null}
          </p>
        )}
        {step === "form" && (
          <form onSubmit={requestSms} className="admin_form">
            <label className="admin_label">
              Interne Adresse
              <div style={{ display: "flex", alignItems: "center", gap: "0", marginTop: "6px" }}>
                <input
                  className="admin_input"
                  style={{ flex: 1, minWidth: 0 }}
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                  autoComplete="username"
                  placeholder="name"
                  maxLength={64}
                />
                <span
                  className="admin_input"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    background: "var(--admin-bg-page, #f5f5f5)",
                    border: "1px solid var(--admin-border, #ddd)",
                    borderLeft: "none",
                    padding: "0 8px",
                    color: "var(--admin-text-secondary, #717171)",
                    fontSize: "14px",
                    borderRadius: "0 4px 4px 0",
                  }}>
                  @telephonio.com
                </span>
              </div>
            </label>
            {avail && localPart.trim().length >= 2 ? (
              <p
                style={{
                  fontSize: "12px",
                  margin: "4px 0 0",
                  color: avail.available
                    ? "var(--cn-semantic-success, #008a05)"
                    : "var(--cn-semantic-error, #c13515)",
                }}>
                {avail.available
                  ? "Diese Adresse ist frei."
                  : "Nicht verfuegbar."}
              </p>
            ) : null}
            {submitErr ? <p className="admin_error">{submitErr}</p> : null}
            <button
              type="submit"
              className="admin_btn admin_btn--primary"
              disabled={
                submitting ||
                !localPart.trim() ||
                (avail && avail.available === false)
              }>
              {submitting ? "SMS wird versendet …" : "SMS-Code anfordern"}
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={submitCode} className="admin_form">
            <p className="admin_product_desc" style={{ fontSize: "14px", margin: "0 0 12px" }}>
              SMS gesendet an <strong>{phoneMasked || "deine Telefonnummer"}</strong>
            </p>
            <label className="admin_label">
              Code
              <input
                type="text"
                className="admin_input"
                value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                placeholder="123456"
                maxLength={6}
                disabled={submitting}
              />
            </label>
            {submitErr ? <p className="admin_error">{submitErr}</p> : null}
            <button
              type="submit"
              className="admin_btn admin_btn--primary"
              disabled={submitting || code.length < 6}>
              {submitting ? "Wird geprueft …" : "Konto anlegen und anmelden"}
            </button>
            <p className="admin_back" style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => { setStep("form"); setCode(""); setSubmitErr(""); }}
                style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "inherit" }}>
                &larr; Andere Adresse
              </button>
            </p>
          </form>
        )}
        <p className="admin_back" style={{ marginTop: 16 }}>
          <a href="/">&larr; Zum Anmelden</a>
        </p>
      </div>
    </div>
  );
}
